import { T, useTranslate } from '@tolgee/react';
import { Tooltip } from 'antd';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import EnterprisePlanIcon from '../../../assets/icons/EnterprisePlanIcon';
import theme from '../../../assets/theme';
import { LicenseCoarseLevels, LicenseFineLevels } from '../../../hooks/useAccessBadge';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import { IconWrapper } from '../../_atoms/IconWrapper';

const HighlightedDiv = styled.div`
  display: flex;
  align-items: center;
  border-radius: 5px;
  padding: 5px 5px 5px 0px;
`;

const PlanIndicator = ({ isMobileDrawer }: { isMobileDrawer: boolean }) => {
  const { isReady, promptPaywall, paywallComponent, userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const { t } = useTranslate();

  const handleShowPaywall = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (isReady) {
      promptPaywall({ label: 'Upgrade account', description: 'Plan Indicator' });
    }
  };

  const userLicenseLabel = useMemo(() => {
    if (
      !userAccessBadge ||
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
    ) {
      return t('Free', 'Free');
    } else if (userAccessBadge.license.coarseLevel === LicenseCoarseLevels.premium) {
      return t('Premium', 'Premium');
    } else if (userAccessBadge.license.fineLevel === LicenseFineLevels.trial) {
      return (
        '⌛ ' +
        new Intl.DateTimeFormat('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }).format(new Date(userAccessBadge.license.expiresAt ?? 0))
      );
    } else {
      return t('School plan', 'School plan');
    }
  }, [userAccessBadge, t]);

  const schoolNameTooltipText = useMemo(() => {
    return !userAccessBadge ||
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium ? (
      userAccessBadge?.organization?.name ?? (
        <T keyName="Personal" defaultValue="Personal" />
      )
    ) : userAccessBadge.license.coarseLevel === LicenseCoarseLevels.premium ? (
      <T keyName="Premium" defaultValue="Premium" />
    ) : userAccessBadge.license.fineLevel === LicenseFineLevels.trial ? (
      <T keyName="School Plan Trial" defaultValue="School Plan Trial" />
    ) : (
      userAccessBadge.organization?.name ?? (
        <T keyName="School Plan" defaultValue="School Plan" />
      )
    );
  }, [userAccessBadge]);

  return (
    <div
      onClick={e => {
        //Prevents the dropdown from showing after closing the paywall modal
        e.stopPropagation();
        e.preventDefault();
      }}
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      {paywallComponent}
      <Tooltip
        title={schoolNameTooltipText}
        mouseEnterDelay={
          typeof schoolNameTooltipText === 'string' && schoolNameTooltipText.length > 20
            ? 1
            : 10000
        }
      >
        <p
          style={{
            fontSize: 16,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            width: '100%',
            marginBottom: 0,
          }}
        >
          {schoolNameTooltipText}
        </p>
      </Tooltip>
      <PointedRow $isMobileDrawer={isMobileDrawer}>
        {isReady && (
          <>
            <span
              style={{
                fontSize: 12,
                whiteSpace: 'nowrap',
                background: '#dad5cf',
                color: theme.colors.black,
                fontWeight: '800',
                padding: '2px 5px',
                borderRadius: 5,
                letterSpacing: '0.06rem',
              }}
            >
              {userLicenseLabel}
            </span>
            <div
              style={{
                height: 3,
                width: 3,
                background: theme.colors.greyDark,
                borderRadius: 20,
              }}
            ></div>
            {(!userAccessBadge ||
              userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium) && (
              <HighlightedDiv
                style={{ cursor: 'pointer' }}
                onClick={e => handleShowPaywall(e)}
              >
                <span
                  style={{
                    fontWeight: 'bold',
                    lineHeight: '14px',
                    fontSize: 12,
                    textDecoration: 'underline',
                    color: theme.colors.black,
                    marginRight: 5,
                  }}
                >
                  <T keyName="Upgrade" defaultValue="Upgrade" />
                </span>
                <IconWrapper height="10px" width="10px" color={theme.colors.yellow}>
                  <EnterprisePlanIcon color={theme.colors.black} />
                </IconWrapper>
              </HighlightedDiv>
            )}
            {(userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise ||
              userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.premium) && (
              <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                <T keyName="Unlimited AI" defaultValue="Unlimited AI" />
              </span>
            )}
          </>
        )}
      </PointedRow>
    </div>
  );
};

export default PlanIndicator;

const PointedRow = styled.div<{ $isMobileDrawer: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  height: 20px;

  ${({ $isMobileDrawer }) =>
    $isMobileDrawer
      ? css`
          color: ${theme.colors.white};
        `
      : css`
          color: ${theme.colors.greyDark};
        `}
`;
